import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{
    error: Error;
    resetError: () => void;
  }>;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Update state with error info
    this.setState({
      error,
      errorInfo,
    });

    // Here you could also send error to logging service
    // e.g., sendErrorToLoggingService(error, errorInfo);
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // If a custom fallback component is provided, use it
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return (
          <FallbackComponent
            error={this.state.error!}
            resetError={this.resetError}
          />
        );
      }

      // Default error UI
      return (
        <DefaultErrorFallback
          error={this.state.error!}
          resetError={this.resetError}
        />
      );
    }

    return this.props.children;
  }
}

interface DefaultErrorFallbackProps {
  error: Error;
  resetError: () => void;
}

const DefaultErrorFallback: React.FC<DefaultErrorFallbackProps> = ({
  error,
  resetError,
}) => {
  const { t } = useTranslation();

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  const isDevelopment = process.env.NODE_ENV === 'development';

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-6">
      <div className="container max-w-2xl mx-auto">
        <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-2xl">
          <CardHeader className="text-center pb-6">
            <div className="mx-auto w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="h-8 w-8 text-destructive" />
            </div>
            <CardTitle className="text-2xl md:text-3xl text-foreground">
              {t('error.title')}
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-6">
            <Alert variant="destructive" className="text-left">
              <Bug className="h-4 w-4" />
              <AlertDescription className="font-medium">
                {t('error.message')}
              </AlertDescription>
            </Alert>

            <div className="text-center text-muted-foreground">
              <p>
                We're sorry for the inconvenience. Please try refreshing the
                page or return to the home page.
              </p>
            </div>

            {/* Error Details (Development Only) */}
            {isDevelopment && (
              <div className="space-y-4">
                <details className="bg-muted/50 p-4 rounded border">
                  <summary className="cursor-pointer font-medium text-sm text-muted-foreground hover:text-foreground">
                    View Error Details (Development)
                  </summary>
                  <div className="mt-4 space-y-2">
                    <div>
                      <p className="text-sm font-medium text-destructive">
                        Error:
                      </p>
                      <code className="text-xs font-mono bg-destructive/10 p-2 rounded block text-destructive">
                        {error.message}
                      </code>
                    </div>
                    {error.stack && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">
                          Stack Trace:
                        </p>
                        <pre className="text-xs font-mono bg-muted p-2 rounded overflow-auto max-h-32 text-muted-foreground">
                          {error.stack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button
                onClick={resetError}
                size="lg"
                className="bg-learning-primary hover:bg-learning-primary/90"
              >
                <RefreshCw className="h-5 w-5 mr-2" />
                {t('error.tryAgain')}
              </Button>
              <Button
                variant="outline"
                onClick={handleReload}
                size="lg"
                className="bg-white/50 backdrop-blur-sm"
              >
                <RefreshCw className="h-5 w-5 mr-2" />
                {t('error.reloadPage')}
              </Button>
              <Button
                variant="outline"
                onClick={handleGoHome}
                size="lg"
                className="bg-white/50 backdrop-blur-sm"
              >
                <Home className="h-5 w-5 mr-2" />
                {t('error.goHome')}
              </Button>
            </div>

            {/* Report Issue */}
            <div className="pt-6 border-t border-border/20 text-center">
              <p className="text-sm text-muted-foreground mb-3">
                {t('error.persistsMessage')}
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const subject = encodeURIComponent(
                    'Error Report - Lingua Craft'
                  );
                  const body = encodeURIComponent(
                    `Error Details:\n\nMessage: ${error.message}\n\nURL: ${window.location.href}\n\nUser Agent: ${navigator.userAgent}\n\nPlease describe what you were doing when this error occurred:\n\n`
                  );
                  window.open(
                    `mailto:support@linguacraft.app?subject=${subject}&body=${body}`
                  );
                }}
                className="text-learning-primary hover:text-learning-primary/80"
              >
                <Bug className="h-4 w-4 mr-2" />
                {t('error.reportIssue')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ErrorBoundary;
